import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";

export default function Lots() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [lots, setLots] = useState([]);
  const [building, setBuilding] = useState({});

  const loadLots = () => api.get("/lots").then((r) => setLots(r.data));
  useEffect(() => {
    loadLots();
    api.get("/building").then((r) => setBuilding(r.data));
  }, []);

  const lots_sorted = [...lots].sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));

  return (
    <div className="space-y-4" data-testid="lots-page">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/more")} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center" data-testid="lots-back">
          <Icons.ArrowLeft className="h-5 w-5 rtl:rotate-180" />
        </button>
        <h1 className="text-2xl font-bold">{t("building")}</h1>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="relative rounded-2xl overflow-hidden aspect-[3/4] bg-secondary" data-testid="building-photo-facade">
          <img src="/building/facade.webp" alt={t("the_building")} loading="lazy"
            className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2.5">
            <span className="text-white text-xs font-bold">{t("the_building")}</span>
          </div>
        </div>
        <div className="relative rounded-2xl overflow-hidden aspect-[3/4] bg-secondary" data-testid="building-photo-entrance">
          <img src="/building/entrance.webp" alt={t("entrance")} loading="lazy"
            className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2.5">
            <span className="text-white text-xs font-bold">{t("entrance")}</span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-brand text-white p-5 relative overflow-hidden">
        <div className="absolute inset-0 zellige-overlay" />
        <div className="relative z-10">
          <p className="font-bold text-lg">{building.name}</p>
          <p className="text-white/70 text-sm">{building.address}</p>
          <div className="flex flex-wrap gap-4 mt-3 text-sm">
            <span><b>{building.floors}</b> {t("floor")}</span>
            <span><b>{lots.length}</b> {t("dwellings")}</span>
          </div>
          {(building.common_areas || []).length > 0 && (
            <div className="mt-4" data-testid="common-areas">
              <p className="text-white/70 text-xs font-semibold uppercase tracking-wide">{t("common_areas")}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {building.common_areas.map((c) => (
                  <span key={c} data-testid={`common-area-${c}`}
                    className="text-xs font-semibold bg-white/15 px-2.5 py-1 rounded-full">
                    {t("ca_" + c)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <h2 className="font-bold text-lg">{lots.length} {t("dwellings")}</h2>
      <div className="space-y-2">
        {lots_sorted.map((l) => <LotRow key={l.id} lot={l} t={t} onChanged={loadLots} />)}
      </div>
    </div>
  );
}

function LotRow({ lot, t, onChanged }) {
  const [busy, setBusy] = useState(false);

  const askTenant = async () => {
    setBusy(true);
    try {
      await api.post(`/residents/${lot.owner_user_id}/request-contact`);
      toast.success(t("lot_request_sent"));
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const occ = lot.occupancy;
  return (
    <div className="rounded-xl bg-card border border-border p-3" data-testid={`lot-${lot.id}`}>
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-accent flex items-center justify-center font-bold text-brand dark:text-primary shrink-0">
          {lot.number}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{lot.owner_name}</p>
          <p className="text-xs text-muted-foreground">{t(lot.type || "duplex")} · {lot.floor} · {lot.tantiemes} ‰</p>
        </div>
        {occ === "occupant" && (
          <span data-testid={`lot-occ-occupant-${lot.number}`}
            className="text-xs bg-brand/10 text-brand dark:text-primary font-semibold px-2 py-0.5 rounded-full shrink-0">
            {t("lot_occupant_badge")}
          </span>
        )}
        {occ === "loue" && (
          <span data-testid={`lot-occ-loue-${lot.number}`}
            className="text-xs bg-majorelle/15 text-majorelle font-semibold px-2 py-0.5 rounded-full shrink-0">
            {t("rented")}
          </span>
        )}
        {!occ && lot.is_rented && <span className="text-xs bg-majorelle/15 text-majorelle font-semibold px-2 py-0.5 rounded-full shrink-0">{t("rented")}</span>}
      </div>

      {occ === "loue" && lot.has_tenant && (
        <div className="mt-2 ps-14" data-testid={`lot-tenant-${lot.number}`}>
          {lot.tenant_status === "visible" ? (
            <div className="flex items-center justify-between gap-2 rounded-lg bg-accent px-3 py-2">
              <p className="text-xs min-w-0">
                <span className="text-muted-foreground">{t("lot_tenant")} : </span>
                <span className="font-semibold">{lot.tenant_name || "—"}</span>
                {lot.tenant_phone && <span dir="ltr" className="text-muted-foreground"> · {lot.tenant_phone}</span>}
              </p>
              {lot.tenant_phone && (
                <a href={`tel:${lot.tenant_phone}`} data-testid={`lot-tenant-call-${lot.number}`}
                  className="h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center shrink-0">
                  <Icons.Phone className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          ) : lot.tenant_status === "pending" ? (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid={`lot-tenant-pending-${lot.number}`}>
              <Icons.Clock className="h-3.5 w-3.5" /> {t("lot_request_pending")}
            </p>
          ) : (
            <button onClick={askTenant} disabled={busy} data-testid={`lot-tenant-ask-${lot.number}`}
              className="text-xs font-semibold text-majorelle flex items-center gap-1.5 disabled:opacity-50">
              <Icons.LockKeyhole className="h-3.5 w-3.5" /> {t("lot_ask_tenant")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
